import { Menu, MenuItem, Typography, Tooltip, styled } from '@mui/material';
import { MIconButton } from 'components/@material-extend';
import { useRef, useState, useMemo, ReactNode } from 'react';
import AreYouSureModal, { AreYouSureModalType } from './AreYouSureModal';
import { startWithUppercase } from 'utils/convertString';
import Icon from 'components/icons/Icon';
import AccessGuard, { AccessRule } from 'access';
import { IconType } from '../../@types/icons';
import { IconEdit, IconDelete } from '@nps.today/component-library';
import useIsMountedRef from 'hooks/useIsMountedRef';
import { useTranslator } from 'translation/useTranslator';

type Action = {
  label: string;
  action: () => void;
  access?: AccessRule;
  icon?: IconType | keyof typeof IconType | ReactNode;
};

type CustomMenuItem = {
  onClick: () => void;
  title: string;
  icon: ReactNode;
};

type MoreMenuButtonProps = {
  itemType: string;
  onEdit?: () => void;
  onEditAccess?: AccessRule;
  onDisplay?: () => void;
  onSendAgain?: () => void;
  onMail?: () => void;
  onUnsubscribe?: () => void;
  onDelete?: (() => void) | (() => Promise<any>);
  onDeleteAccess?: AccessRule;
  deleteAreYouSureContent?: ReactNode | string;
  onTest?: () => void;
  onClone?: () => void;
  onInfo?: () => void;
  onOpen?: (() => void) | null;
  onClose?: (() => void) | null;
  closeAreYouSureContent?: ReactNode | string;
  onDownload?: (() => void) | null;
  customMenuItems?: CustomMenuItem[];
  onMenuClick?: (opened: boolean) => void;
  useDeleteConfirmationSafeguard?: boolean;
};

const renderActionIcon = (icon: Action['icon']) =>
  IconType[icon as keyof typeof IconType] ? (
    <Icon type={icon as keyof typeof IconType} />
  ) : (
    <>{icon}</>
  );

const MoreMenuButton = ({
  itemType,
  onEdit,
  onEditAccess,
  onDisplay,
  onSendAgain,
  onUnsubscribe,
  onMail,
  onDelete,
  onDeleteAccess,
  deleteAreYouSureContent,
  onTest,
  onClone,
  onInfo,
  onOpen,
  onClose,
  closeAreYouSureContent,
  onDownload,
  customMenuItems,
  onMenuClick,
  useDeleteConfirmationSafeguard
}: MoreMenuButtonProps) => {
  const translator = useTranslator();
  const menuRef = useRef(null);
  const isMountedRef = useIsMountedRef();
  const [open, setOpen] = useState(false);
  const [displayAreYouSureModal, setDisplayAreYouSureModal] = useState(false);
  const [areYouSureModalAction, setAreYouSureModalAction] = useState<() => void>(() => {});
  const [areYouSureModalTitle, setAreYouSureModalTitle] = useState('');
  const [areYouSureModalDescription, setAreYouSureModalDescription] = useState<string | ReactNode>(
    ''
  );
  const [areYouSureModalType, setAreYouSureModalType] =
    useState<keyof typeof AreYouSureModalType>('Continue');

  const handleOpen = () => {
    setOpen(true);
    onMenuClick?.(true);
  };

  const handleClose = () => {
    setOpen(false);
    onMenuClick?.(false);
  };

  const onActionThatNeedsConfirmation = ({
    action,
    title,
    description,
    type
  }: {
    action: () => void;
    title: string;
    description: string | ReactNode;
    type: keyof typeof AreYouSureModalType;
  }) => {
    setAreYouSureModalAction(() => action);
    setAreYouSureModalTitle(title);
    setAreYouSureModalDescription(description);
    setDisplayAreYouSureModal(true);
    setAreYouSureModalType(type);
  };

  const IconWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.5rem',
    height: '1.5rem'
  });

  const handleClick = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  const actions = useMemo<Action[]>(() => {
    const arr: Action[] = [];
    if (onEdit)
      arr.push({
        label: translator.edit(),
        action: onEdit,
        icon: <IconEdit />,
        access: onEditAccess
      });
    if (onDisplay) arr.push({ label: translator.display(), action: onDisplay, icon: 'display' });
    if (onSendAgain)
      arr.push({
        label: translator.resendSurvey(),
        action: () =>
          onActionThatNeedsConfirmation({
            action: onSendAgain,
            title: translator.sendAgainTitle(),
            description: translator.sendAgainDescription('1'),
            type: 'Continue'
          }),
        icon: 'resendsurvey'
      });
    if (onMail) arr.push({ label: translator.sendEmail(), action: onMail, icon: 'email' });
    if (onUnsubscribe)
      arr.push({
        label: translator.unsubscribe(),
        action: () =>
          onActionThatNeedsConfirmation({
            action: onUnsubscribe,
            title: translator.removeFromListTitle(),
            description: translator.removeFromListDescription(),
            type: 'Continue'
          }),
        icon: 'unsubscribe'
      });
    if (onTest) arr.push({ label: translator.testType(itemType), action: onTest, icon: 'beta' });
    if (onClone)
      arr.push({
        label: translator.cloneTypeTitle(itemType),
        action: () =>
          onActionThatNeedsConfirmation({
            action: onClone,
            title: translator.cloneTypeTitle(itemType),
            description: translator.cloneTypeDescription(itemType),
            type: 'Continue'
          }),
        icon: 'copy'
      });
    if (onInfo)
      arr.push({
        label: translator.typeInfo(startWithUppercase(itemType)),
        action: onInfo,
        icon: 'question'
      });
    if (onOpen)
      arr.push({ label: translator.openType(itemType), action: onOpen, icon: 'campaignactive' });
    if (onClose)
      arr.push({
        label: translator.closeTypeTitle(itemType),
        action: () =>
          onActionThatNeedsConfirmation({
            action: onClose,
            title: translator.closeTypeTitle(itemType),
            description: closeAreYouSureContent || translator.closeTypeDescription(itemType),
            type: 'Continue'
          }),
        icon: 'campaignclosed'
      });
    if (onDelete)
      arr.push({
        label: translator.deleteType(itemType),
        action: () =>
          onActionThatNeedsConfirmation({
            action: onDelete,
            title: translator.deleteType(itemType),
            description: deleteAreYouSureContent || translator.deleteTypeDescription(itemType),
            type: 'Delete'
          }),
        icon: <IconDelete />,
        access: onDeleteAccess
      });
    if (onDownload)
      arr.push({ label: translator.downloadType(itemType), action: onDownload, icon: 'download' });
    if (customMenuItems) {
      customMenuItems.forEach((customItem) => {
        arr.push({
          label: customItem.title,
          action: customItem.onClick,
          icon: customItem.icon // Assuming customItem.icon is the type of icon
        });
      });
    }
    return arr;
  }, [
    onEdit,
    translator,
    onEditAccess,
    onDisplay,
    onSendAgain,
    onMail,
    onUnsubscribe,
    onTest,
    itemType,
    onClone,
    onInfo,
    onOpen,
    onClose,
    onDelete,
    onDeleteAccess,
    onDownload,
    customMenuItems,
    closeAreYouSureContent,
    deleteAreYouSureContent
  ]);

  const { action, icon, label } = actions?.[0];
  return (
    <>
      {actions.length === 1 && (
        <div onClick={(event) => event.stopPropagation()}>
          <Tooltip title={label}>
            <MIconButton size="large" onClick={action}>
              {renderActionIcon(icon)}
            </MIconButton>
          </Tooltip>
        </div>
      )}
      {actions.length > 1 && (
        <div onClick={(event) => event.stopPropagation()}>
          <MIconButton ref={menuRef} size="large" onClick={handleOpen}>
            <Icon type="moremenu" />
          </MIconButton>
          <Menu
            open={open}
            anchorEl={menuRef.current}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            style={{ display: 'flex', flexWrap: 'wrap' }}
          >
            <div>
              {customMenuItems?.map((menuItem, index) => (
                <MenuItem key={index} onClick={menuItem.onClick}>
                  <IconWrapper>{menuItem.icon}</IconWrapper>

                  <Typography variant="body2" sx={{ ml: 2 }}>
                    {menuItem.title}
                  </Typography>
                </MenuItem>
              ))}

              {actions.map((action, index) => (
                <AccessGuard key={index} rule={action.access}>
                  <MenuItem onClick={handleClick(action.action)}>
                    <IconWrapper>{renderActionIcon(action.icon)}</IconWrapper>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {action.label}
                    </Typography>
                  </MenuItem>
                </AccessGuard>
              ))}
            </div>
          </Menu>
        </div>
      )}
      <AreYouSureModal
        useConfirmationSafeguard={
          useDeleteConfirmationSafeguard && areYouSureModalType === AreYouSureModalType.Delete
        }
        type={areYouSureModalType}
        display={displayAreYouSureModal}
        hideModal={() => {
          if (!isMountedRef.current) return;
          setDisplayAreYouSureModal(false);
        }}
        title={areYouSureModalTitle}
        description={areYouSureModalDescription}
        onContinue={areYouSureModalAction}
      />
    </>
  );
};

export default MoreMenuButton;
